import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { showError, showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { handlePossibleNonFieldError } from 'api/query-client';

import {
  connectMarketplaceTemplate,
  disconnectIntegration,
  getMarketplaceIntegrationLists,
  getMarketplaceIntegrationsConnected,
  getMarketplaceIntegrationsNotConnected,
  getMarketplaceTemplate,
  getMarketplaceTemplates,
  getTemplateEndpoint,
  getTemplateIntegrationJourneys,
  getTemplateWebhook,
  MarketplaceIntegration,
  MarketplaceIntegrationWithLists,
  MarketplaceTemplate,
  MarketplaceTemplateFilters,
  reconnectIntegration,
  testIntegrationConnection,
} from './requests';

const templatesKey = (filters?: MarketplaceTemplateFilters) =>
  ['app-marketplace-templates', filters?.status ?? 'all', filters?.search ?? ''].filter(
    (item) => item !== undefined
  );

const templateKey = (id: string) => ['app-marketplace-template', id];
const templateJourneysKey = (id: string) => ['app-marketplace-template-journeys', id];
const integrationsKey = (status?: 'connected' | 'not-connected') =>
  status ? ['app-marketplace-integrations', status] : ['app-marketplace-integrations'];

export const useMarketplaceTemplates = (filters?: MarketplaceTemplateFilters) => {
  return useQuery({
    queryKey: templatesKey(filters),
    queryFn: () => getMarketplaceTemplates(filters),
    keepPreviousData: true,
  });
};

export const useMarketplaceTemplate = (id: string | undefined) => {
  const queryClient = useQueryClient();

  return useQuery<MarketplaceTemplate>({
    queryKey: templateKey(id ?? ''),
    queryFn: () => getMarketplaceTemplate(id ?? ''),
    enabled: Boolean(id),
    onSuccess: (data) => {
      if (
        data.connectionStatus === 'connected' &&
        data.validationUrl &&
        !data.skipCredentialValidation &&
        data.connectedIntegrationId
      ) {
        testIntegrationConnection(data.connectedIntegrationId, { skipIfRecent: true })
          .then((result) => {
            if (!result.success) {
              queryClient.invalidateQueries(templateKey(id ?? ''));
            }
          })
          .catch(() => {
            queryClient.invalidateQueries(templateKey(id ?? ''));
          });
      }
    },
  });
};

export const useTemplateIntegrationJourneys = (templateId: string | undefined) => {
  return useQuery({
    queryKey: templateJourneysKey(templateId ?? ''),
    queryFn: () => getTemplateIntegrationJourneys(templateId ?? ''),
    enabled: Boolean(templateId),
  });
};

export const useTemplateEndpoint = (shortId: string) => {
  return useQuery({
    queryKey: ['template-endpoint', shortId],
    queryFn: () => getTemplateEndpoint(shortId),
    enabled: Boolean(shortId),
  });
};

export const useTemplateWebhook = (shortId: string) => {
  return useQuery({
    queryKey: ['template-webhook', shortId],
    queryFn: () => getTemplateWebhook(shortId),
    enabled: Boolean(shortId),
  });
};

export const useConnectMarketplaceTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: connectMarketplaceTemplate,
    onSuccess: (_, { templateId }) => {
      queryClient.invalidateQueries({ queryKey: templatesKey() });
      queryClient.invalidateQueries({ queryKey: templateKey(templateId) });
      queryClient.invalidateQueries({ queryKey: integrationsKey() });
    },
    onError: (e: unknown) => {
      handlePossibleNonFieldError(e, t('failedToConnect'));
    },
  });
};

export const useMarketplaceIntegrationsConnected = () => {
  return useQuery<MarketplaceIntegration[]>({
    queryKey: integrationsKey('connected'),
    queryFn: getMarketplaceIntegrationsConnected,
  });
};

export const useMarketplaceIntegrationsNotConnected = () => {
  return useQuery<MarketplaceIntegration[]>({
    queryKey: integrationsKey('not-connected'),
    queryFn: getMarketplaceIntegrationsNotConnected,
  });
};

export const useDisconnectIntegration = (templateId: string) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templatesKey() });
      queryClient.invalidateQueries({ queryKey: templateKey(templateId) });
      queryClient.invalidateQueries({ queryKey: integrationsKey() });
    },
    onError: (e: unknown) => {
      handlePossibleNonFieldError(e, t('failedToDisconnect'));
    },
  });
};

export const useTestIntegrationConnection = () => {
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: testIntegrationConnection,
    onSuccess: (data) => {
      if (data.success) {
        showSuccess(t('connectionTestSuccessful'));
      } else {
        showError(`${t('connectionTestFailed')} (${data.status_code})`);
      }
    },
    onError: (e: unknown) => {
      handlePossibleNonFieldError(e, t('failedToTestConnection'));
    },
  });
};

export const useReconnectIntegration = (templateId: string) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reconnectIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templatesKey() });
      queryClient.invalidateQueries({ queryKey: templateKey(templateId) });
      queryClient.invalidateQueries({ queryKey: integrationsKey() });
    },
    onError: (e: unknown) => {
      handlePossibleNonFieldError(e, t('failedToReconnect'));
    },
  });
};

export const useInvalidateQueriesAfterConnect = (templateId: string) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: templatesKey() });
    queryClient.invalidateQueries({ queryKey: templateKey(templateId) });
    queryClient.invalidateQueries({ queryKey: ['app-marketplace-integrations-connected'] });
    queryClient.invalidateQueries({ queryKey: ['app-marketplace-integrations-not-connected'] });
    queryClient.invalidateQueries({ queryKey: ['app-marketplace-template'] });
    queryClient.invalidateQueries({ queryKey: ['journey-action'] });
    queryClient.invalidateQueries({ queryKey: ['journey-actions'] });
    queryClient.invalidateQueries({ queryKey: ['journey-integration-endpoints'] });
    queryClient.invalidateQueries({ queryKey: ['journey-integration-webhooks'] });
    queryClient.invalidateQueries({ queryKey: ['journey'] });
  };
};

export const useMarketplaceIntegrationLists = () => {
  return useQuery<MarketplaceIntegrationWithLists[]>({
    queryKey: ['marketplace-integration-lists'],
    queryFn: getMarketplaceIntegrationLists,
  });
};

const locale = {
  en: {
    failedToConnect: 'Failed to connect',
    failedToDisconnect: 'Failed to disconnect',
    connectionTestSuccessful: 'Connection test successful',
    connectionTestFailed: 'Connection test failed',
    failedToTestConnection: 'Failed to test connection',
    failedToReconnect: 'Failed to reconnect',
  },
};
